import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { normalizeJsonApi } from '../../utils/entity-states/json-api-normalizer';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);

  private baseUrl = environment.apiUrl;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  getData(
    resource: string,
    customParams?: Record<string, unknown>,
  ): Observable<any> {
    let params = new HttpParams();

    if (customParams) {
      for (const key in customParams) {
        const value = customParams[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const subObj = value as Record<string, unknown>;
          for (const subKey in subObj) {
            if (Object.prototype.hasOwnProperty.call(subObj, subKey)) {
              const subValue = subObj[subKey];
              if (subValue !== null && subValue !== undefined) {
                params = params.set(
                  `${key}[${subKey}]`,
                  String(subValue),
                );
              }
            }
          }
        }
        else if (value !== null && value !== undefined) {
          params = params.set(key, String(value));
        }
      }
    }

    const res$ = this.http
      .get<any>(`${this.baseUrl}/${resource}`, { params });

    return res$.pipe(map((response) => normalizeJsonApi(response)));
  }

  getDataSingle(
    resource: string,
    id: string,
    customParams?: Record<string, string | number>,
  ): Observable<any> {
    let params = new HttpParams();

    if (customParams) {
      for (const key in customParams) {
        params = params.set(key, customParams[key].toString());
      }
    }

    const res$ = this.http
      .get<any>(`${this.baseUrl}/${resource}/${id}`, { params });

    return res$.pipe(map((response) => normalizeJsonApi(response)));
  }

  createDataSingle(resource: string, data: unknown): Observable<any> {
    const url = `${this.baseUrl}/${resource}`;
    const dataObj = data as { data?: { relationships?: Record<string, unknown> } };

    // Detect relationships automatically
    let params = new HttpParams();
    if (dataObj?.data?.relationships) {
      const include = Object.keys(dataObj.data.relationships).join(',');
      params = params.set('include', include);
    }

    // Set JSON:API Content-Type header
    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json',
    });

    const res$ = this.http
      .post<any>(url, data, { headers, params });

    return res$.pipe(map((response) => normalizeJsonApi(response)));
  }

  updateDataSingle(resource: string, id: string, data: unknown): Observable<any> {
    const url = `${this.baseUrl}/${resource}/${id}`;
    const dataObj = data as { data?: { relationships?: Record<string, unknown> } };

    // Detect relationships to automatically include
    let params = new HttpParams();
    if (dataObj?.data?.relationships) {
      const include = Object.keys(dataObj.data.relationships).join(','); // keep camelCase
      params = params.set('include', include);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json',
    });

    const res$ = this.http
      .patch<any>(url, data, { headers, params });

    return res$.pipe(map((response) => normalizeJsonApi(response)));
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  deleteDataSingle(resource: string, id: string) {
    const url = `${this.baseUrl}/${resource}/${id}`;
    const headers = new HttpHeaders({
      Accept: 'application/vnd.api+json',
    });

    return this.http.delete<void>(url, { headers });
  }
}
